/**
 * @file Renders the modal that creates a new entity through a form.
 */
import React, { useState } from "react";
import type { FormInstance } from "antd";

import { EntityForm, useForm } from "@components/Form";
import type { AppContext } from "@components/AppContext/types";
import type { Operation } from "@api";

import Modal from "./Modal";
import type { ModalRef, TableRef } from "./types";

type EntityFormPassthroughProps = {
  entity: string;
  operation: Operation;
  schema?: unknown;
  item?: unknown;
  request?: AppContext["request"];
  submitLabel?: string;
  onSubmit?: (parameters: unknown) => unknown;
  submitButtonProps?: Record<string, unknown>;
  successMessage?: string | boolean;
  failedMessage?: string | boolean;
  shouldOptimizeUpdateMutation?: boolean;
  expectedErrors?: string[];
  formProps?: Record<string, unknown>;
  hasSubmitButton?: boolean;
  onFailure?: () => void;
  onSubmitFailed?: () => void;
  onSubmitConfirm?: (parameters: unknown, action: () => unknown) => unknown;
  submitItemProps?: Record<string, unknown>;
};

type Props = {
  form?: FormInstance;
  onSuccess?: (createdItem: unknown) => unknown;
  table?: TableRef;
  width?: number | string;
  footer?: () => React.ReactNode;
  onExit?: () => void;
  modal: ModalRef;
  title: string;
} & EntityFormPassthroughProps;

/**
 * Renders a modal with an entity-creation form and adds the created item
 * to the given table on success.
 */
const CreateModal = ({
  form: customForm = undefined,
  onSuccess: onSuccessCustom = undefined,
  table = undefined,
  width = undefined,
  footer = undefined,
  onExit = undefined,
  modal,
  title,
  ...otherEntityFormProps
}: Props): JSX.Element => {
  const form = useForm(customForm);

  const [ isOpen, setIsOpen ] = useState(false);

  /**
   * Closes the modal and resets the form.
   */
  const onClose = () => {
    if (onExit) {
      onExit();
    }

    setIsOpen(false);
    form.resetFields();
  };

  /**
   * Runs the custom success handler, adds the created item to the table,
   * resets the form and closes the modal.
   */
  const onSuccess = async (createdItem: unknown) => {
    if (onSuccessCustom) {
      await onSuccessCustom(createdItem);
    }

    if (table) {
      table.current.addItem(createdItem);
    }

    form.resetFields();
    return onClose();
  };

  /**
   * Opens the modal.
   */
  modal.current.open = () =>
    setIsOpen(true);

  const modalProps = {
    forceRender: true,
    destroyOnClose: true,
    width,
    title,
    isOpen,
    onClose,
  };

  return (
    <Modal {...modalProps}>
      <EntityForm
        form={form}
        onSuccess={onSuccess}
        {...otherEntityFormProps}
      />

      {footer && footer()}
    </Modal>
  );
};

export default CreateModal;
